<template>
  <div>
    <div id="app" v-if="initiated" class="conteneur" :class="iconTheme">
      <span v-if="$store.state.showVersion === 'yes'" class="version">v383</span>

      <Menu ref="menu" v-if="showMenu && $store.state.profile != null" />

      <Alertes ref="alertes" />
      <div v-bind:class="{ inside: showMenu }">
        <BandeauPass
          :class="{ bandeauPass: true, hideBandeauMobile: !showBandeauMobile }"
          v-if="$store.state.bandeau && showBandeau && $store.state.promo == null"
        />
        <keep-alive>
          <router-view />
        </keep-alive>
      </div>
      <Footer :inside="showMenu" />
    </div>
    <div v-if="reconnecting" class="reconnecting">
      <div class="contenuReco">
        <p>Votre connexion à Idylive perdue...</p>
        <p>Vous pouvez cliquer sur le bouton ci-dessous pour tenter de vous reconnecter.</p>
        <div>
          <button class="bouton" @click="reco">Reconnexion</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Menu from "@/components/Menu";
import Footer from "@/components/Footer";
import Alertes from "@/components/util/Alertes";
import BandeauPass from "@/components/BandeauPass";
import { idyuRetrieveSession, idyuConnect, idyuJsonRpc, idyuReconnect } from "@/assets/js/idyu/connect.js";
import { idyuInit } from "@/assets/js/idyu/init.js";

export default {
  name: "App",

  metaInfo: {
    title: "Idylive"
  },

  components: {
    Menu,
    Alertes,
    Footer,
    BandeauPass
  },

  data() {
    return {
      initiated: false,
      noMenuRoutes: ["Inscription", "Index", "404", "Admin", "payment", "confirmation"],
      noBandeauRoutes: [
        "Check",
        "Serenite",
        "Pass",
        "Inscription",
        "Index",
        "404",
        "Admin",
        "payment",
        "confirmation",
        "Promo"
      ],
      noBandeauRoutesMobile: []
    };
  },

  created: async function() {
    this.$global.pageInitiated = this.initApp();
    await this.$global.pageInitiated;
  },

  beforeMount: function() {
    this.$store.state.windowWidth = window.innerWidth;
    this.updateCssVh();
  },

  mounted: function() {
    window.onresize = () => {
      this.$store.state.windowWidth = window.innerWidth;
      this.updateCssVh();
    };

    window.addEventListener("click", () => {
      if (this.$refs.menu) this.$refs.menu.hideMenuMobile();
    });
  },

  computed: {
    reconnecting() {
      if (!this.$store.state.messager) {
        return false;
      }
      return this.$store.state.messager.sessionDisconnected;
    },

    showMenu: function() {
      for (let elt of this.noMenuRoutes) {
        if (this.$route.name != null && this.$route.name.includes(elt)) return false;
      }
      return true;
    },

    showBandeau: function() {
      for (let elt of this.noBandeauRoutes) {
        if (this.$route.name != null && this.$route.name.includes(elt)) return false;
      }
      return true;
    },

    showBandeauMobile: function() {
      if (!this.showBandeau) {
        return false;
      }
      for (let elt of this.noBandeauRoutesMobile) {
        if (this.$route.name != null && this.$route.name.includes(elt)) return false;
      }
      return true;
    },

    iconTheme: function() {
      return this.$store.state.iconTheme;
    }
  },

  methods: {
    initApp: async function() {
      let sessionData = await idyuRetrieveSession(this);

      if (!sessionData) {
        // Maybe we have stored identifiers, let's try to connect using those if there are any
        if (localStorage.login != null && localStorage.password != null) {
          const loggedIn = await idyuConnect(localStorage.login, localStorage.password);
          if (loggedIn === 0) {
            sessionData = await idyuRetrieveSession(this);
          }
        }
      }

      if (!sessionData) {
        this.initiated = true;
        if (this.$route.name != "Inscription") {
          this.$router.push({ name: "Inscription" });
        }
      } else {
        await idyuInit(this.$store, sessionData, this.$router);
        if (this.$router.currentRoute.name === "Inscription") {
          this.$router.push({ name: "Profil" }, () => {
            this.initiated = true;
          });
        } else {
          this.initiated = true;
        }
      }

      // Init promo
      try {
        const promo = await idyuJsonRpc("myPromo");
        this.$store.state.promo = promo;
      } catch (e) {
        console.error(e);
        this.$store.state.promo = null;
      }
    },

    async reco() {
      try {
        let res = await idyuReconnect();
        if (res == 0) {
          window.location.reload();
        } else {
          await alert("La reconnexion a échoué... Veuillez réessayer ultérieurement");
        }
      } catch {
        await alert("La reconnexion a échoué... Veuillez réessayer ultérieurement");
      }
    },

    updateCssVh: function() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }
  }
};
</script>

<style lang="scss">
@import "@/assets/css/boutons.scss";
@import "@/assets/css/base.scss";
@import "@/assets/css/vars.scss";
@import "@/assets/css/photos.scss";
@import "@/assets/css/quill.scss";
@import "@/assets/css/tabs.scss";

html {
  overscroll-behavior: none;
}

a.pinkLink {
  font-style: italic;
  text-decoration: underline;
  color: $theme_color;
  cursor: pointer;
}

.pinklive {
  font-weight: bold;
  color: $theme_color;
}

.pseudo {
  position: relative;
}

.message .body p {
  margin: 0;
}

.reconnecting {
  z-index: 1000;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: black;
  opacity: 0.9;
  display: grid;
  align-items: center;
  justify-items: center;

  .contenuReco {
    padding: 10px;
    width: 300px;
    height: 200px;
    background-color: white;
    display: grid;
    align-items: center;
    justify-items: center;
    text-align: center;
  }
}

#app {
  max-width: $app_width;
  margin: 0 auto;
  background-color: $background_color;
  padding: 10px;
  padding-top: 0px;
  margin-top: 20px;
}

@media screen and (max-width: $app_width) {
  #app {
    margin-top: 0px;
  }
}

button.bouton {
  padding-left: 10px;
  padding-right: 10px;
}

.admin {
  table {
    border-collapse: collapse;
  }
  td {
    border: 1px #aaa solid;
    padding: 5px;
    text-align: center;
  }

  th {
    text-align: center;
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>
